import { FaCashRegister } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { useGetEventCashRegisterMovementsQuery } from '../store';
import FilterTable, { BaseData } from './Table/Table';
import EmptyTable from './Table/EmptyTable';
import { parseFriendlyDateAndHour } from '../utils/dates';
import { formatCurrency } from '../utils/shared';
import CustomModal from './atoms/CustomModal';
import CreateEditCashMovement from './CreateEditCashMovement';
import Button from './atoms/Button';
import useGetUserRole from '../hooks/useGetUserRole';

interface TableRow extends BaseData {
  date: string;
  amount: string;
  type: string;
  user: string;
}

interface CashMovementTableProps {
  businessId: string;
  eventId: string;
}

const CashMovementTable = ({ businessId, eventId }: CashMovementTableProps) => {
  const { cashier_id: cashRegisterId, session_id: sessionId } = useParams();
  const { data } = useGetEventCashRegisterMovementsQuery({
    businessId,
    eventId,
    cashRegisterId: cashRegisterId || '',
    sessionId: sessionId || '',
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const role = useGetUserRole();

  const getType = (type: string) => {
    if (type === 'cash_in' || type === 'float_in') {
      return ('Ingreso');
    }
    if (type === 'cash_out' || type === 'float_out') {
      return ('Egreso');
    }
    if (type === 'sale') {
      return ('Venta');
    }
    return ('Movimiento');
  };

  const rows: TableRow[] = data?.map((movement) => ({
    id: movement.id,
    date: parseFriendlyDateAndHour(movement.created_at, true),
    amount: formatCurrency(movement.amount),
    type: getType(movement.type),
    user: movement.performed_by_user?.username || movement.performed_by_user_id,
  })) || [];

  const headCells = [
    { id: 'date', label: 'Fecha' },
    { id: 'user', label: 'Usuario' },
    { id: 'amount', label: 'Monto' },
    { id: 'type', label: 'Tipo' },
  ];

  const customButton = <Button onClick={() => setIsModalOpen(true)} variant='secondary'>
    <div className='flex flex-row gap-2 items-center'>
      <img src={'/coins-hand.svg'} alt="Registrar movimiento" className='min-w-[20px] min-h-[20px] w-5 h-5 invert' />
      <p className='whitespace-nowrap hidden md:block'>Registrar movimiento</p>
    </div>
  </Button>;

  return (
    <div>
      {data && data?.length > 0 ? (
        <FilterTable
          rows={rows}
          headCells={headCells}
          tableId="cash-movement-table"
          customButtons={(role === 'ADMIN' || role === 'ROOT') ? [customButton] : undefined}
          pagination={{ enabled: true, pageSize: 10 }}
        />
      ) : (
        <EmptyTable
          title="No hay movimientos"
          info="No hay movimientos en esta sesión"
          icon={FaCashRegister}
          iconColor="text-primary-500"
          button={(role === 'ADMIN' || role === 'ROOT') ? [
            {
              label: 'Registrar movimiento',
              onClick: () => setIsModalOpen(true),
              variant: 'secondary',
              image: '/coins-hand.svg',
              imageInverted: true,
            },
          ] : undefined}
        />
      )}
      <CustomModal isOpen={isModalOpen} onRequestClose={() => setIsModalOpen(false)} hideCancelButton>
        <CreateEditCashMovement eventId={eventId} businessId={businessId} cashRegisterId={cashRegisterId || ''} sessionId={sessionId || ''} onClose={() => setIsModalOpen(false)} />
      </CustomModal>
    </div>
  );
};

export default CashMovementTable;
